import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  b2bApi,
  type B2BCustomerListParams,
  type CreateB2BCustomerInput,
  type UpdateB2BCustomerInput,
  type PriceListListParams,
  type CreatePriceListInput,
  type CreditListParams,
  type B2BInvoiceListParams,
  type SalesRepListParams,
  type CreateSalesRepInput,
  type SupplierListParams,
  type CreateSupplierInput,
  type VolumeDiscountListParams,
  type CreateVolumeDiscountInput,
} from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  customers: "b2b-customers",
  priceLists: "b2b-price-lists",
  priceListItems: (id: string) => ["b2b-price-list-items", id],
  credit: "b2b-credit",
  invoices: "b2b-invoices",
  aging: "b2b-aging",
  reps: "b2b-reps",
  suppliers: "b2b-suppliers",
  volumeDiscounts: "b2b-volume-discounts",
} as const;

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export function useB2BCustomers(params: B2BCustomerListParams = {}) {
  return useQuery({
    queryKey: [KEYS.customers, params],
    queryFn: () => b2bApi.customers.list(params),
    staleTime: 30_000,
  });
}

export function useB2BCustomerStats() {
  return useQuery({
    queryKey: [KEYS.customers, "stats"],
    queryFn: () => b2bApi.customers.stats(),
    staleTime: 30_000,
  });
}

function invalidateCustomers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [KEYS.customers] });
}

export function useCreateB2BCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateB2BCustomerInput) =>
      b2bApi.customers.create(input),
    onSuccess: () => invalidateCustomers(qc),
  });
}

export function useUpdateB2BCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateB2BCustomerInput }) =>
      b2bApi.customers.update(id, input),
    onSuccess: () => invalidateCustomers(qc),
  });
}

export function useSuspendB2BCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, activate }: { id: string; activate: boolean }) =>
      activate ? b2bApi.customers.activate(id) : b2bApi.customers.suspend(id),
    onSuccess: () => invalidateCustomers(qc),
  });
}

// ---------------------------------------------------------------------------
// Price lists
// ---------------------------------------------------------------------------

export function usePriceLists(params: PriceListListParams = {}) {
  return useQuery({
    queryKey: [KEYS.priceLists, params],
    queryFn: () => b2bApi.priceLists.list(params),
    staleTime: 30_000,
  });
}

export function useCreatePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePriceListInput) =>
      b2bApi.priceLists.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.priceLists] });
    },
  });
}

export function usePriceListItems(id: string) {
  return useQuery({
    queryKey: KEYS.priceListItems(id),
    queryFn: () => b2bApi.priceLists.items(id),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Credit applications
// ---------------------------------------------------------------------------

export function useCreditApplications(params: CreditListParams = {}) {
  return useQuery({
    queryKey: [KEYS.credit, params],
    queryFn: () => b2bApi.credit.list(params),
    staleTime: 30_000,
  });
}

export function useApproveCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => b2bApi.credit.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.credit] });
      qc.invalidateQueries({ queryKey: [KEYS.customers] });
    },
  });
}

export function useRejectCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      b2bApi.credit.reject(id, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.credit] });
    },
  });
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export function useB2BInvoices(params: B2BInvoiceListParams = {}) {
  return useQuery({
    queryKey: [KEYS.invoices, params],
    queryFn: () => b2bApi.invoices.list(params),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Aging report
// ---------------------------------------------------------------------------

export function useAgingReport() {
  return useQuery({
    queryKey: [KEYS.aging],
    queryFn: () => b2bApi.aging.report(),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Sales reps
// ---------------------------------------------------------------------------

export function useSalesReps(params: SalesRepListParams = {}) {
  return useQuery({
    queryKey: [KEYS.reps, params],
    queryFn: () => b2bApi.reps.list(params),
    staleTime: 30_000,
  });
}

export function useCreateSalesRep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSalesRepInput) => b2bApi.reps.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.reps] });
    },
  });
}

// ---------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------

export function useSuppliers(params: SupplierListParams = {}) {
  return useQuery({
    queryKey: [KEYS.suppliers, params],
    queryFn: () => b2bApi.suppliers.list(params),
    staleTime: 30_000,
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSupplierInput) => b2bApi.suppliers.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.suppliers] });
    },
  });
}

// ---------------------------------------------------------------------------
// Volume discounts
// ---------------------------------------------------------------------------

export function useVolumeDiscounts(params: VolumeDiscountListParams = {}) {
  return useQuery({
    queryKey: [KEYS.volumeDiscounts, params],
    queryFn: () => b2bApi.volumeDiscounts.list(params),
    staleTime: 30_000,
  });
}

export function useCreateVolumeDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVolumeDiscountInput) =>
      b2bApi.volumeDiscounts.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.volumeDiscounts] });
    },
  });
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

export function useB2BImport() {
  return useMutation({
    mutationFn: (file: File) => b2bApi.import.upload(file),
  });
}
